import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, setStoredToken, googleExchange, googleNativeExchange, appleNativeExchange } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import { isIosApp, isAndroidORIosApp } from 'webtonative';
import { socialLogin } from 'webtonative/SocialLogin';
import { FcGoogle } from 'react-icons/fc';
import { FaApple } from 'react-icons/fa';
import { Phone, Mail, User, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import OtpResendButton from '../components/OtpResendButton';

const LOGO_URL = `${process.env.PUBLIC_URL}/branding/genzox-logo.png`;

const Login = () => {
  // Iter44: phone-login flow now has FOUR modes instead of three:
  //   choose → phone (enter number) → [name (new users only)] → otp → email-prompt
  // We check the phone existence server-side BEFORE deciding to ask for the
  // user's name. Existing users never see the Name field.
  const [mode, setMode] = useState('choose'); // choose | phone | name | otp | email-prompt | email-login | email-otp
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  // iter104e — Continue with Email flow state. Kept separate from `otp` so a
  // user who abandons phone flow mid-way doesn't leak state into email flow.
  const [emailOtp, setEmailOtp] = useState('');
  // 409 prompt when email is claimed via Google (Option B).
  const [emailGooglePrompt, setEmailGooglePrompt] = useState(null);
  // Per-method loading flags. A previous bug used a single `loading` flag
  // which caused ALL sign-in buttons to show "Signing In…" when the user
  // tapped any one of them — Apple reviewers flagged this on iPad. We now
  // isolate the spinner to the button the user actually pressed.
  //   googleLoading → "Continue with Google" button
  //   appleLoading  → "Continue with Apple"  button
  //   phoneLoading  → OTP + phone flow buttons (send-otp / verify)
  // A composite `loading` is derived to disable other buttons while any
  // auth flow is in progress (prevents concurrent multi-provider races).
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const loading = googleLoading || appleLoading || phoneLoading;
  // iter104b — Merge-account prompt state. When verify_otp returns 409 with
  // code `email_already_registered`, we stash the prompt info here to
  // render the confirmation modal; on user confirm the OTP-verify call is
  // retried with `merge_with_existing_email_account=true`.
  const [mergePrompt, setMergePrompt] = useState(null);
  // iter104b — Guard against double-submit on the OTP verify button.
  // React state updates are async, so a rapid double-tap (common in
  // Android WebView wrappers where taps sometimes fire twice due to
  // touchstart+click) can trigger handleVerifyOTP twice before the
  // `phoneLoading` flag propagates to disable the button. The FIRST
  // call consumes the OTP; the SECOND call then sees no OTP in DB and
  // returns "Invalid OTP" — a spurious error that hides the successful
  // login. useRef gives us a synchronous lock unaffected by React's
  // render cadence.
  const verifyingRef = useRef(false);
  // After /auth/check-phone resolves: true → existing user (skip name step),
  // false → new user (collect name then send OTP), null → not yet checked.
  const [phoneExists, setPhoneExists] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // iter89 — Post-login return path. Anywhere in the app can redirect to
  // /login?next=/some/where and we'll bounce the user back there after a
  // successful sign-in of ANY provider (phone / Google / Apple). Guarded
  // against open-redirect: only same-origin relative paths are honoured.
  const nextPath = (() => {
    const raw = searchParams.get('next') || '';
    if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
    return '/';
  })();
  const { setUser, checkAuth } = useAuth();

  // iter141 — Slow-network UX: after 10 s of a Google sign-in still hanging,
  // surface a "Still signing you in…" info toast so users on flaky mobile
  // data don't think the button is dead. The toast auto-dismisses when the
  // flow resolves either way (success or error) via the returned clearer.
  const _startSlowSignInToast = (label = 'Google') => {
    const timer = setTimeout(() => {
      toast.info(`Still signing you in… slow network, hang tight`, {
        id: 'slow-signin',
        duration: 12000,
      });
    }, 10000);
    return () => { clearTimeout(timer); toast.dismiss('slow-signin'); };
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Custom Google OAuth using GenzoX's own Google Cloud credentials (not Emergent-managed).
  // Uses popup flow → returns auth code → backend exchanges for tokens & creates session.
  const handleGoogleLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'popup',
    onSuccess: async (codeResponse) => {
      setGoogleLoading(true);
      const stopSlowToast = _startSlowSignInToast('Google');
      try {
        // For popup flow, Google expects redirect_uri="postmessage"
        const res = await googleExchange(codeResponse.code, 'postmessage');
        if (res.data?.session_token) setStoredToken(res.data.session_token);
        setUser(res.data);
        toast.success(`Welcome, ${res.data.name}!`);
        navigate(nextPath);
      } catch (e) {
        console.error('Google exchange failed', e?.response?.data || e);
        const detail = e?.response?.data?.detail;
        toast.error(detail ? `Google sign-in failed: ${detail}` : 'Google sign-in failed. Please try again.');
      } finally {
        stopSlowToast();
        setGoogleLoading(false);
      }
    },
    onError: (e) => {
      console.error('Google login error', e);
      toast.error('Google sign-in was cancelled');
    },
  });


  // Native Google Sign-In via WebToNative SDK — used ONLY when the app is
  // running inside the Android/iOS wrapper (WebView-based). The popup flow
  // (`@react-oauth/google`) relies on `window.open()`, which the WebView
  // silently blocks — tapping the button appears to do nothing at all.
  // The WebToNative native bridge opens the OS-native Google account picker
  // and posts back a callback with `{ isSuccess, idToken, ... }`.
  //
  // ── iter102 (restore July 4th behavior) ──
  // No fallback logic, no pair-code, no timeout, no Custom Tab. Just the
  // clean, stable native SDK path that worked before the S26/Fold 5 work.
  // Any device-specific edge cases (Fold 5 native SDK hang) will be handled
  // separately with a proper cross-platform solution.
  const handleNativeGoogleLogin = () => {
    setGoogleLoading(true);
    // iter130 — Some Android devices fail to fire the WebToNative
    // `socialLogin.google.login` callback for secondary / non-default Google
    // accounts (only the primary account triggers the callback correctly).
    // Symptom: user selects a secondary account → the "Signing in" button
    // spins forever because `setGoogleLoading(false)` never runs. Hard
    // client-side timeout: if no callback fires within 30s, reset the UI and
    // show an actionable error so the user can retry (typically after
    // signing out of the extra Google accounts from the OS-level picker).
    let settled = false;
    const abortTimer = setTimeout(() => {
      if (settled) return;
      settled = true;
      setGoogleLoading(false);
      toast.error(
        'Google sign-in did not complete for that account. Please try again — if it keeps happening, remove the extra Google accounts from the picker and retry with your primary account.',
        { duration: 8000 },
      );
    }, 30000);
    socialLogin.google.login({
      callback: async (data) => {
        if (settled) return; // Callback arrived after timeout — ignore.
        settled = true;
        clearTimeout(abortTimer);
        const stopSlowToast = _startSlowSignInToast('Google');
        try {
          // WebToNative documented callback shape:
          //   success → { isSuccess: true,  idToken: '...', type: 'googleLoginToken' }
          //   failure → { isSuccess: false, error: '...',  type: 'googleLoginToken' }
          if (data?.isSuccess === false) {
            toast.error(data?.error || 'Google sign-in was cancelled');
            return;
          }
          const idToken = data?.idToken || data?.id_token;
          if (!idToken) {
            toast.error(data?.error || data?.message || 'Google sign-in was cancelled');
            return;
          }
          const res = await googleNativeExchange(idToken);
          if (res.data?.session_token) setStoredToken(res.data.session_token);
          setUser(res.data);
          toast.success(`Welcome, ${res.data.name}!`);
          navigate(nextPath);
        } catch (e) {
          console.error('Native Google exchange failed', e?.response?.data || e);
          const detail = e?.response?.data?.detail;
          toast.error(detail ? `Google sign-in failed: ${detail}` : 'Google sign-in failed. Please try again.');
        } finally {
          stopSlowToast();
          setGoogleLoading(false);
        }
      },
    });
  };

  // Routes the "Continue with Google" button to the right flow based on
  // whether we are inside the WebToNative wrapper or a regular browser.
  //
  // Why we need TWO code paths:
  //  • Browsers (desktop + mobile Chrome/Safari) — support `window.open()`
  //    so Google's popup flow works cleanly.
  //  • WebToNative wrapper (Android + iOS apps) — WebView BLOCKS
  //    `window.open()`, making the popup silently fail with no error. The
  //    native SDK bridge is the ONLY way to get the Google account picker
  //    to actually surface on the device.
  const onGoogleButtonClick = () => {
    if (isAndroidORIosApp) {
      handleNativeGoogleLogin();
    } else {
      handleGoogleLogin();
    }
  };

  // Sign in with Apple via WebToNative SDK (iOS-only). App Store policy
  // requires this option whenever Google sign-in is offered. The native SDK
  // returns { isSuccess, idToken, firstName, lastName, emailId } — name/email
  // are ONLY populated on first-ever consent for this Apple ID + app pair.
  //
  // ⚠️ For this to work end-to-end, the iOS app MUST have the
  // "Sign in with Apple" capability enabled in:
  //   1. Apple Developer Portal → App ID `in.genzox.app` → Capabilities
  //   2. WebToNative dashboard → iOS → Sign in with Apple plugin (enabled)
  // Without those, iOS aborts the request with error code 1001 (treated by
  // AuthenticationServices as "canceled") before the auth sheet even appears.
  const handleAppleLogin = () => {
    setAppleLoading(true);
    console.info('[Apple] starting sign-in via WebToNative SDK');
    // iter130 — Same defensive client-side timeout pattern as Google native.
    // If the native SDK ever drops the callback (observed occasionally when
    // Apple's Sign-In sheet is dismissed by the OS mid-flow), the "Signing
    // in" button would otherwise spin forever.
    let settled = false;
    const abortTimer = setTimeout(() => {
      if (settled) return;
      settled = true;
      setAppleLoading(false);
      toast.error('Apple sign-in did not complete. Please try again.', { duration: 6000 });
    }, 30000);
    socialLogin.apple.login({
      // Request name + email scopes — Apple only returns these on the FIRST
      // consent so we must ask for them up front, otherwise we never see a
      // display name back from Apple.
      scope: 'name email',
      callback: async (data) => {
        if (settled) return; // Callback arrived after timeout — ignore.
        settled = true;
        clearTimeout(abortTimer);
        console.info('[Apple] native callback:', JSON.stringify({
          isSuccess: data?.isSuccess,
          hasIdToken: !!data?.idToken,
          error: data?.error,
          type: data?.type,
        }));
        try {
          if (data?.isSuccess === false) {
            const rawErr = String(data?.error || '');
            // ASAuthorizationError code 1001 = "canceled" — but iOS also
            // emits this when the Sign in with Apple capability is missing
            // from the app's entitlements. Show a more actionable message
            // so reviewers/users know to retry or contact support.
            const looksLikeConfigError = rawErr.includes('1001') || rawErr.toLowerCase().includes('authorization');
            if (looksLikeConfigError) {
              toast.error('Apple sign-in was cancelled or temporarily unavailable. Please try again.');
            } else {
              toast.error(rawErr || 'Apple sign-in was cancelled');
            }
            return;
          }
          const idToken = data?.idToken || data?.id_token;
          if (!idToken) {
            toast.error(data?.error || 'Apple sign-in was cancelled');
            return;
          }
          const res = await appleNativeExchange(idToken, {
            firstName: data?.firstName,
            lastName: data?.lastName,
            emailId: data?.emailId,
          });
          if (res.data?.session_token) setStoredToken(res.data.session_token);
          setUser(res.data);
          toast.success(`Welcome, ${res.data.name || 'Apple User'}!`);
          navigate(nextPath);
        } catch (e) {
          console.error('Apple exchange failed', e?.response?.data || e);
          const detail = e?.response?.data?.detail;
          toast.error(detail ? `Apple sign-in failed: ${detail}` : 'Apple sign-in failed. Please try again.');
        } finally {
          setAppleLoading(false);
        }
      },
    });
  };

  // Apple Sign-In button is only shown inside the WebToNative iOS app (App
  // Store policy doesn't apply to Android, and the web doesn't have native
  // Apple sign-in via this SDK).
  const isIosNativeApp = isIosApp;

  // Step 1: user enters phone → we check whether they're an existing user.
  // Existing → straight to OTP. New → ask for Name first, then OTP.
  const handleCheckPhone = async () => {
    if (phone.length < 10) { toast.error('Enter a valid 10-digit number'); return; }
    setPhoneLoading(true);
    try {
      const formatted = `+91${phone}`;
      // iter104 — Parallelize check-phone + send-otp. Previously these were
      // sequential (`await check` → `await send`), doubling the RTT wait
      // on mobile networks (~600ms vs ~300ms). They have no data
      // dependency: the send-otp call doesn't need the check result. Run
      // them concurrently to halve the perceived "OTP sent" latency.
      const [checkRes, _sendRes] = await Promise.allSettled([
        authAPI.checkPhone(formatted),
        authAPI.sendOTP(formatted),
      ]);
      // Fail open on check errors → assume new user (safer default: we may
      // collect a name unnecessarily but we never wrongly skip the name
      // field for a brand-new user). Send failures are ignored — test-mode
      // OTP is logged server-side and verify path still works.
      const exists = checkRes.status === 'fulfilled' ? !!checkRes.value?.data?.exists : false;
      setPhoneExists(exists);
      if (exists) {
        setMode('otp');
        toast.success('OTP sent! Welcome back.');
      } else {
        setMode('name');
      }
    } finally { setPhoneLoading(false); }
  };

  // Step 2 (new user only): collect Name, optionally Email, then go to OTP.
  const handleSubmitName = () => {
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    setMode('otp');
    toast.success('OTP sent to your phone');
  };

  const handleVerifyOTP = async (mergeChoiceRaw = undefined) => {
    if (otp.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    // iter104f — Defensive guard: React's `onClick={handleVerifyOTP}` (without
    // an arrow wrapper) passes the SyntheticEvent as the first argument.
    // That event contains circular refs to the DOM/React fiber, so if it
    // reached axios' JSON.stringify it would throw
    //   "TypeError: Converting circular structure to JSON"
    // BEFORE the request ever left the browser — the user then sees our
    // "Network error, please check your connection" toast even though there
    // was no network call. All button click-sites now use () => handleVerifyOTP(),
    // and this guard normalizes any accidental non-boolean argument to
    // `undefined` so the same bug can never re-emerge from another callsite.
    const mergeChoice = (mergeChoiceRaw === true || mergeChoiceRaw === false)
      ? mergeChoiceRaw
      : undefined;
    // iter104b — Synchronous double-submit guard. See verifyingRef comment
    // above. This runs BEFORE any await so a rapid second tap is a no-op.
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setPhoneLoading(true);
    try {
      const formatted = `+91${phone}`;
      // Iter44: for existing users we send no name/email to ensure the backend
      // never overwrites their saved profile. Backend's `verify_otp` already
      // skips `$set:{name}` when the field isn't supplied — see /auth/verify-otp.
      const sendName = phoneExists === false ? name.trim() : '';
      const sendEmail = phoneExists === false ? (email.trim() || undefined) : undefined;
      const res = await authAPI.verifyOTP(formatted, otp, sendName, sendEmail, mergeChoice);
      // iter104e — Blur focused input AFTER the network call returns. Doing it
      // before caused the OTP autofill on some Android WebViews to lose its
      // pending value if the DOM re-rendered during the request.
      try { document.activeElement?.blur?.(); } catch (_) {}
      // Persist Bearer token immediately — survives even if React state updates fail
      if (res.data?.session_token) {
        try { setStoredToken(res.data.session_token); } catch (_) {}
      }
      // EVERY successful OTP-verify takes the user to the homepage via a hard
      // page navigation. We deliberately bypass React Router (navigate) and React
      // state transitions (setMode) on the success path because iOS WebKit has
      // race conditions between (a) state-driven mode switches, (b) autoFocus on
      // new inputs and (c) the soft-keyboard closing animation — any of which can
      // produce a blank screen. Hard nav guarantees a clean SPA remount with the
      // session_token already in localStorage.
      const needsEmail = !res.data?.email;
      if (!needsEmail) {
        toast.success(`Welcome, ${res.data.name}!`);
      }
      // iter89 — Honour ?next= for post-OTP redirect. If ?next was passed
      // and the user still needs to provide an email, we tack the email
      // prompt onto the next path so the redirect logic works either way.
      const nextWithEmail = needsEmail
        ? (nextPath.includes('?') ? `${nextPath}&prompt_email=1` : `${nextPath}?prompt_email=1`)
        : nextPath;
      window.requestAnimationFrame(() => {
        window.location.href = nextWithEmail;
      });
      // Safety net: if rAF never fires (extremely rare), force navigation after 600ms
      setTimeout(() => {
        if (window.location.pathname.startsWith('/login')) {
          window.location.href = nextWithEmail;
        }
      }, 600);
    } catch (e) {
      console.error('OTP verify failed', e?.response?.data || e);
      // iter103 — Handle "email already registered" 409. Backend returned
      // structured `detail` describing the existing account; render a
      // merge-confirmation modal instead of a raw toast error.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === 'object' && detail.code === 'email_already_registered') {
        setMergePrompt({
          email: detail.email || '',
          existingName: detail.existing_name || '',
          methods: detail.existing_auth_methods || [],
        });
        setPhoneLoading(false);
        verifyingRef.current = false;
        return;
      }
      // iter104e — Distinguish server errors ("Invalid OTP") from transport
      // errors (network drop, DNS failure, timeout). Axios sets
      // e.response only when a full HTTP response was received; if it's
      // missing, the request never reached the server (or the server never
      // responded) — telling the user "Invalid OTP" would be misleading.
      let errMsg;
      if (!e?.response) {
        errMsg = 'Network error, please check your connection';
      } else {
        errMsg = (typeof detail === 'string') ? detail : (detail?.message || 'Invalid OTP');
      }
      toast.error(errMsg);
      setPhoneLoading(false);
      verifyingRef.current = false;
    }
  };

  const handleSaveEmail = async () => {
    if (email.trim() && !email.includes('@')) { toast.error('Enter a valid email'); return; }
    if (email.trim()) {
      try {
        // Use the api axios instance (with Bearer header) instead of fetch — on
        // iPhone Chrome the cross-site Set-Cookie may not propagate yet, so the
        // Bearer fallback from localStorage is the reliable auth carrier.
        await authAPI.updateProfile({ email: email.trim() });
        await checkAuth();
      } catch (e) { /* proceed anyway — email is optional */ }
    }
    toast.success('Welcome to GenzoX!');
    // Same iOS-safe full-page redirect as the verify-OTP success path
    window.location.href = '/';
  };

  // ── iter104e — Continue with Email flow ────────────────────────────
  const handleSendEmailOTP = async () => {
    const clean = email.trim().toLowerCase();
    if (!clean || !clean.includes('@') || !clean.split('@')[1]?.includes('.')) {
      toast.error('Enter a valid email');
      return;
    }
    setPhoneLoading(true);
    try {
      await authAPI.emailLoginSendOTP(clean);
      setEmailOtp('');
      setMode('email-otp');
      toast.success('OTP sent to your email');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (!e?.response) {
        toast.error('Network error, please check your connection');
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to send email OTP'));
      }
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyEmailOTP = async () => {
    if (emailOtp.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setPhoneLoading(true);
    try {
      const clean = email.trim().toLowerCase();
      // For new email accounts, allow an optional display name — reuses the
      // phone-flow `name` state if the user already typed one, else backend
      // falls back to the email local-part.
      const displayName = name.trim() || undefined;
      const res = await authAPI.emailLoginVerifyOTP(clean, emailOtp, displayName);
      try { document.activeElement?.blur?.(); } catch (_) {}
      if (res.data?.session_token) {
        try { setStoredToken(res.data.session_token); } catch (_) {}
      }
      toast.success(`Welcome, ${res.data.name}!`);
      window.requestAnimationFrame(() => { window.location.href = nextPath; });
      setTimeout(() => {
        if (window.location.pathname.startsWith('/login')) {
          window.location.href = nextPath;
        }
      }, 600);
    } catch (e) {
      console.error('Email OTP verify failed', e?.response?.data || e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Option B (per user's spec) — email is claimed via Google. Show a
      // dedicated prompt inviting the user to sign in with Google instead.
      if (status === 409 && detail && typeof detail === 'object' && detail.code === 'email_registered_via_google') {
        setEmailGooglePrompt({
          email: detail.email || email.trim().toLowerCase(),
          existingName: detail.existing_name || '',
        });
        setPhoneLoading(false);
        verifyingRef.current = false;
        return;
      }
      // Network vs server error disambiguation (same pattern as phone verify).
      let errMsg;
      if (!e?.response) {
        errMsg = 'Network error, please check your connection';
      } else {
        errMsg = (typeof detail === 'string') ? detail : (detail?.message || 'Invalid OTP');
      }
      toast.error(errMsg);
      setPhoneLoading(false);
      verifyingRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0edff] via-white to-[#fff4ed] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-3xl shadow-2xl shadow-[#1800ad]/5 p-6 sm:p-8" data-testid="login-card">
          <div className="text-center mb-7">
            <img src={LOGO_URL} alt="GenzoX" width="512" height="512" loading="eager" fetchpriority="high" decoding="async" className="h-28 sm:h-32 w-auto mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#0A0B10]" data-testid="login-title">
              {mode === 'email-prompt' ? 'Almost Done!' : 'Welcome to GenzoX'}
            </h1>
            <p className="text-xs text-[#9CA3AF] mt-1">
              {mode === 'choose' && 'Buy & sell with real people near you'}
              {mode === 'phone' && 'Enter your mobile number to continue'}
              {mode === 'name' && 'Welcome! Tell us your name'}
              {mode === 'otp' && `OTP sent to +91 ${phone}`}
              {mode === 'email-prompt' && 'Add your email to receive notifications (optional)'}
              {mode === 'email-login' && 'Enter your email to receive a login code'}
              {mode === 'email-otp' && `OTP sent to ${email}`}
            </p>
          </div>

          {/* Choose Method */}
          {mode === 'choose' && (
            <div className="space-y-3">
              <button data-testid="google-login-btn" onClick={() => onGoogleButtonClick()} disabled={loading}
                className="w-full bg-white border-2 border-gray-200 text-[#0A0B10] rounded-2xl px-5 py-3.5 font-bold hover:border-[#1800ad] hover:shadow-md transition-all flex items-center justify-center gap-3 text-sm disabled:opacity-60">
                <FcGoogle size={20} /> {googleLoading ? 'Signing in...' : 'Continue with Google'}
              </button>

              {isIosNativeApp && (
                <button data-testid="apple-login-btn" onClick={handleAppleLogin} disabled={loading}
                  className="w-full bg-black text-white rounded-2xl px-5 py-3.5 font-bold hover:bg-[#1a1a1a] transition-all flex items-center justify-center gap-3 text-sm disabled:opacity-60 mt-3">
                  <FaApple size={20} /> {appleLoading ? 'Signing in...' : 'Continue with Apple'}
                </button>
              )}

              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-[10px] text-[#9CA3AF] font-bold uppercase">or</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>

              <button data-testid="phone-login-btn" onClick={() => setMode('phone')}
                className="w-full bg-[#1800ad] text-white rounded-2xl px-5 py-3.5 font-bold hover:bg-[#1200a0] transition-colors shadow-lg shadow-[#1800ad]/20 flex items-center justify-center gap-2 text-sm">
                <Phone size={16} /> Login with Phone
              </button>

              <button data-testid="email-login-btn" onClick={() => { setMode('email-login'); setEmail(''); setEmailOtp(''); }}
                className="w-full bg-white border-2 border-gray-200 text-[#0A0B10] rounded-2xl px-5 py-3.5 font-bold hover:border-[#1800ad] hover:shadow-md transition-all flex items-center justify-center gap-2 text-sm">
                <Mail size={16} /> Continue with Email
              </button>

              <button data-testid="browse-guest-btn" onClick={() => navigate('/')}
                className="w-full text-[#9CA3AF] text-xs font-medium hover:text-[#6B7280] transition-colors mt-3 py-2">
                Browse as Guest
              </button>
            </div>
          )}

          {/* Phone number only — existence check decides next step */}
          {mode === 'phone' && (
            <div className="space-y-4">
              <div>
                <label className="flex items-center gap-1.5 text-xs font-bold text-[#0A0B10] mb-1.5"><Phone size={13} /> Mobile Number</label>
                <div className="flex gap-2">
                  <span className="flex items-center px-3 bg-[#f0edff] rounded-xl text-sm text-[#1800ad] font-bold">+91</span>
                  <input data-testid="phone-input" type="tel" value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit number" maxLength={10} autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter' && phone.length === 10 && !loading) handleCheckPhone(); }}
                    className="flex-1 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none" />
                </div>
              </div>
              <button data-testid="send-otp-btn" onClick={handleCheckPhone} disabled={loading || phone.length < 10}
                className="w-full bg-[#1800ad] text-white rounded-2xl py-3.5 font-bold text-sm hover:bg-[#1200a0] transition-colors shadow-lg shadow-[#1800ad]/20 disabled:opacity-40 flex items-center justify-center gap-2">
                {phoneLoading ? 'Checking...' : <><ArrowRight size={16} /> Continue</>}
              </button>
              <button onClick={() => { setMode('choose'); setPhone(''); setName(''); setPhoneExists(null); }}
                className="w-full text-[#9CA3AF] text-xs font-medium hover:text-[#1800ad] transition-colors py-1">
                Back to login options
              </button>
            </div>
          )}

          {/* Name + (optional) Email — shown ONLY for new users after phone existence check */}
          {mode === 'name' && (
            <div className="space-y-4" data-testid="new-user-name-step">
              <p className="text-xs text-[#4B5563] bg-[#f0edff] rounded-xl px-3 py-2 -mb-1">
                Welcome to GenzoX! Just a couple of details to set up your account.
              </p>
              <div>
                <label className="flex items-center gap-1.5 text-xs font-bold text-[#0A0B10] mb-1.5"><User size={13} /> Full Name</label>
                <input data-testid="name-input" type="text" value={name} onChange={e => setName(e.target.value)}
                  placeholder="Enter your full name" autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) handleSubmitName(); }}
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none" />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-xs font-bold text-[#0A0B10] mb-1.5">Email <span className="text-[#9CA3AF] font-normal normal-case">(optional)</span></label>
                <input data-testid="email-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none" />
              </div>
              <button data-testid="submit-name-btn" onClick={handleSubmitName} disabled={!name.trim()}
                className="w-full bg-[#1800ad] text-white rounded-2xl py-3.5 font-bold text-sm hover:bg-[#1200a0] transition-colors shadow-lg shadow-[#1800ad]/20 disabled:opacity-40 flex items-center justify-center gap-2">
                <ArrowRight size={16} /> Continue to OTP
              </button>
              <button onClick={() => { setMode('phone'); setName(''); setEmail(''); }}
                className="w-full text-[#9CA3AF] text-xs font-medium hover:text-[#1800ad] transition-colors py-1">
                Change number
              </button>
            </div>
          )}

          {/* OTP Verify */}
          {mode === 'otp' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#0A0B10] mb-1.5">Enter 6-digit OTP</label>
                <input data-testid="otp-input" type="tel" inputMode="numeric" autoComplete="one-time-code" value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="------" maxLength={6} autoFocus
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3.5 text-lg text-center tracking-[0.5em] font-bold text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none" />
              </div>
              <button data-testid="verify-otp-btn" onClick={() => handleVerifyOTP()} disabled={loading || otp.length < 6}
                className="w-full bg-[#1800ad] text-white rounded-2xl py-3.5 font-bold text-sm hover:bg-[#1200a0] transition-colors shadow-lg shadow-[#1800ad]/20 disabled:opacity-40">
                {phoneLoading ? 'Verifying...' : 'Verify OTP'}
              </button>
              {/* iter65 — Resend OTP with 30s countdown. */}
              <div className="flex items-center justify-center">
                <OtpResendButton
                  onResend={async () => {
                    try {
                      await authAPI.sendOTP(`+91${phone}`);
                      toast.success('OTP resent to your phone');
                    } catch (e) {
                      const detail = e?.response?.data?.detail;
                      toast.error(detail || 'Failed to resend OTP. Try again.');
                      throw e;
                    }
                  }}
                  testIdPrefix="login-resend"
                />
              </div>
              <button onClick={() => { setMode('phone'); setOtp(''); }}
                className="w-full text-[#9CA3AF] text-xs font-medium hover:text-[#1800ad] transition-colors py-1">
                Change number
              </button>
            </div>
          )}

          {/* Optional Email Prompt (after phone login) */}
          {mode === 'email-prompt' && (
            <div className="space-y-4">
              <div>
                <label className="flex items-center gap-1.5 text-xs font-bold text-[#0A0B10] mb-1.5"><Mail size={13} /> Email Address (Optional)</label>
                <input data-testid="email-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none" />
                <p className="text-[10px] text-[#9CA3AF] mt-1">We'll send notifications about your ads to this email</p>
              </div>
              <button data-testid="save-email-btn" onClick={handleSaveEmail}
                className="w-full bg-[#1800ad] text-white rounded-2xl py-3.5 font-bold text-sm hover:bg-[#1200a0] transition-colors shadow-lg shadow-[#1800ad]/20">
                {email.trim() ? 'Save & Continue' : 'Skip & Continue'}
              </button>
            </div>
          )}

          {/* iter104e — Continue with Email → email input → OTP screen */}
          {mode === 'email-login' && (
            <div className="space-y-4" data-testid="email-login-step">
              <div>
                <label className="flex items-center gap-1.5 text-xs font-bold text-[#0A0B10] mb-1.5"><Mail size={13} /> Email Address</label>
                <input data-testid="email-login-input" type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" autoFocus autoComplete="email"
                  onKeyDown={(e) => { if (e.key === 'Enter' && email.trim() && !loading) handleSendEmailOTP(); }}
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none" />
                <p className="text-[10px] text-[#9CA3AF] mt-1">We'll send a 6-digit login code to this address</p>
              </div>
              <button data-testid="send-email-otp-btn" onClick={handleSendEmailOTP}
                disabled={loading || !email.trim() || !email.includes('@')}
                className="w-full bg-[#1800ad] text-white rounded-2xl py-3.5 font-bold text-sm hover:bg-[#1200a0] transition-colors shadow-lg shadow-[#1800ad]/20 disabled:opacity-40 flex items-center justify-center gap-2">
                {phoneLoading ? 'Sending...' : <><ArrowRight size={16} /> Send OTP</>}
              </button>
              <button onClick={() => { setMode('choose'); setEmail(''); setEmailOtp(''); }}
                className="w-full text-[#9CA3AF] text-xs font-medium hover:text-[#1800ad] transition-colors py-1">
                Back to login options
              </button>
            </div>
          )}

          {mode === 'email-otp' && (
            <div className="space-y-4" data-testid="email-otp-step">
              <div>
                <label className="block text-xs font-bold text-[#0A0B10] mb-1.5">Enter 6-digit OTP</label>
                <input data-testid="email-otp-input" type="tel" inputMode="numeric" autoComplete="one-time-code"
                  value={emailOtp}
                  onChange={e => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="------" maxLength={6} autoFocus
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3.5 text-lg text-center tracking-[0.5em] font-bold text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none" />
              </div>
              <button data-testid="verify-email-otp-btn" onClick={handleVerifyEmailOTP}
                disabled={loading || emailOtp.length < 6}
                className="w-full bg-[#1800ad] text-white rounded-2xl py-3.5 font-bold text-sm hover:bg-[#1200a0] transition-colors shadow-lg shadow-[#1800ad]/20 disabled:opacity-40">
                {phoneLoading ? 'Verifying...' : 'Verify OTP'}
              </button>
              <div className="flex items-center justify-center">
                <OtpResendButton
                  onResend={async () => {
                    try {
                      await authAPI.emailLoginSendOTP(email.trim().toLowerCase());
                      toast.success('OTP resent to your email');
                    } catch (e) {
                      const detail = e?.response?.data?.detail;
                      toast.error(detail || 'Failed to resend OTP. Try again.');
                      throw e;
                    }
                  }}
                  testIdPrefix="email-login-resend"
                />
              </div>
              <button onClick={() => { setMode('email-login'); setEmailOtp(''); }}
                className="w-full text-[#9CA3AF] text-xs font-medium hover:text-[#1800ad] transition-colors py-1">
                Change email
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs sm:text-sm text-[#4B5563] font-medium mt-4">
          By continuing, you agree to our <a href="/terms" className="text-[#1800ad] font-semibold hover:underline">Terms</a> & <a href="/privacy" className="text-[#1800ad] font-semibold hover:underline">Privacy Policy</a>
        </p>
      </div>

      {/* iter103 — Merge-account confirmation modal.
          Triggered when verify_otp returns 409 { code: 'email_already_registered' }.
          User can (a) merge into the existing account, (b) cancel & change email. */}
      {mergePrompt && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setMergePrompt(null); }}
          data-testid="merge-account-modal"
        >
          <div className="bg-white rounded-2xl sm:rounded-3xl w-full sm:max-w-md p-5 sm:p-7 shadow-2xl">
            <div className="w-11 h-11 rounded-full bg-[#f0edff] flex items-center justify-center mb-3">
              <Mail size={22} className="text-[#1800ad]" />
            </div>
            <h3 className="text-lg font-bold text-[#0A0B10]">Account already exists</h3>
            <p className="text-sm text-[#4B5563] mt-2">
              An account with <span className="font-semibold text-[#0A0B10]">{mergePrompt.email}</span>
              {mergePrompt.existingName ? <> (<span className="font-semibold">{mergePrompt.existingName}</span>)</> : null}
              {' '}already exists
              {mergePrompt.methods && mergePrompt.methods.length > 0 ? (
                <> — previously signed in with <span className="font-semibold text-[#0A0B10]">{mergePrompt.methods.join(', ')}</span></>
              ) : null}.
            </p>
            <p className="text-sm text-[#4B5563] mt-2">
              Do you want to <span className="font-semibold text-[#0A0B10]">merge this phone number</span> into that account? Your listings, subscription and history will be preserved.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 mt-5">
              <button
                type="button"
                data-testid="merge-confirm-btn"
                onClick={() => { setMergePrompt(null); handleVerifyOTP(true); }}
                className="flex-1 rounded-full bg-[#1800ad] hover:bg-[#1200a0] text-white px-4 py-2.5 text-sm font-bold transition-colors"
              >
                Yes, merge accounts
              </button>
              <button
                type="button"
                data-testid="merge-cancel-btn"
                onClick={() => setMergePrompt(null)}
                className="flex-1 rounded-full bg-white border-2 border-gray-200 hover:border-gray-300 text-[#0A0B10] px-4 py-2.5 text-sm font-bold transition-colors"
              >
                Use a different email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iter104e — Email-registered-via-Google 409 modal (Option B).
          Shown when Continue-with-Email is attempted for an email already
          tied to a Google Sign-In account. Invites the user to use Google
          instead — we do NOT auto-merge because Google accounts have
          stronger trust than a raw email OTP. */}
      {emailGooglePrompt && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setEmailGooglePrompt(null); }}
          data-testid="email-google-prompt-modal"
        >
          <div className="bg-white rounded-2xl sm:rounded-3xl w-full sm:max-w-md p-5 sm:p-7 shadow-2xl">
            <div className="w-11 h-11 rounded-full bg-[#f0edff] flex items-center justify-center mb-3">
              <FcGoogle size={22} />
            </div>
            <h3 className="text-lg font-bold text-[#0A0B10]">Email already registered</h3>
            <p className="text-sm text-[#4B5563] mt-2">
              <span className="font-semibold text-[#0A0B10]">{emailGooglePrompt.email}</span>
              {emailGooglePrompt.existingName ? <> (<span className="font-semibold">{emailGooglePrompt.existingName}</span>)</> : null}
              {' '}is already registered via Google.
            </p>
            <p className="text-sm text-[#4B5563] mt-2">
              Please sign in with Google to access your account.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 mt-5">
              <button
                type="button"
                data-testid="email-google-signin-btn"
                onClick={() => { setEmailGooglePrompt(null); onGoogleButtonClick(); }}
                className="flex-1 rounded-full bg-[#1800ad] hover:bg-[#1200a0] text-white px-4 py-2.5 text-sm font-bold transition-colors flex items-center justify-center gap-2"
              >
                <FcGoogle size={16} /> Sign in with Google
              </button>
              <button
                type="button"
                data-testid="email-google-cancel-btn"
                onClick={() => setEmailGooglePrompt(null)}
                className="flex-1 rounded-full bg-white border-2 border-gray-200 hover:border-gray-300 text-[#0A0B10] px-4 py-2.5 text-sm font-bold transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
